import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import CurrentSession from "data/userSession";

// material-ui
import { useTheme } from "@mui/material/styles";
import { APP_IDENTITY } from "config/app";
import { AppBar, Box, CssBaseline, Toolbar } from "@mui/material";
import { Capacitor } from "@capacitor/core";
// project imports
import Header from "./Header";
import BottomTabBar from "./BottomTabBar";
import AuthGuard from "./AuthGuard";
import { LayoutProvider } from "./LayoutContext";
import ProgressOverlayHost from "features/progress/Overlay";

// Reachable signed-out. Everything else requires auth (AuthGuard toasts + redirects
// to /login). Includes the Help/directory hub, the copy pages (privacy, terms, how-
// to-use = about), and support/membership so a logged-out visitor can read them.
const PUBLIC_PATHS = [
  "/login",
  "/privacy",
  "/terms",
  "/delete",
  "/directory",
  "/about",
  "/support",
  "/membership",
];

// Height reserved at the bottom for the fixed AdBanner / native admob overlay.
const AD_BANNER_HEIGHT = Capacitor.getPlatform() === "ios" ? 66 : 50;

// ==============================|| MAIN LAYOUT ||============================== //
//
// A plain in-flow flex column: header / main / tab bar / (ad spacer). The shell
// stays mounted; only <Outlet/> swaps between routes. No runtime measurement --
// CSS sizes everything, so pages (esp. Tug) fit exactly from any entry path.

const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const [supporter, setSupporter] = useState(true);
  const [isDemoUser, setIsDemoUser] = useState(false);

  // Pages get standard content padding by default; the Tug page opts out so it
  // can fill edge-to-edge.
  const defaultPadding = !location.pathname.startsWith("/tug");
  const isPublicPath = PUBLIC_PATHS.some((p) =>
    location.pathname.startsWith(p)
  );

  useEffect(() => {
    CurrentSession.load().then((user: any) => {
      setSupporter(!!user?.supporter);
      setIsDemoUser(user?.uid === APP_IDENTITY.demoUserId);
    });
  }, []);

  const showAds = !supporter || isDemoUser;
  // Space the tab bar reserves below its buttons for the ad banner (matches the
  // old layout's offsets). 5px keeps a small gap when no ad is shown.
  const adBannerOffset = showAds ? AD_BANNER_HEIGHT : 5;

  // Scroll restoration for the persistent <main> scroller. Browsers don't do
  // this for SPA route changes, so returning to a list (feed, community) would
  // otherwise snap to the top. We save scrollTop per history entry (location.key)
  // and restore it on return; a fresh forward navigation starts at the top.
  const mainRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef<Map<string, number>>(new Map());
  const prevPathname = useRef(location.pathname);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const key = location.key;
    const onScroll = () => scrollPositions.current.set(key, el.scrollTop);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [location.key]);

  useLayoutEffect(() => {
    // Opening/closing a profile overlay is a same-pathname navigation (it just
    // adds a history entry). The list underneath must not move, so skip scroll
    // handling entirely unless the actual page changed.
    if (location.pathname === prevPathname.current) return;
    prevPathname.current = location.pathname;

    const el = mainRef.current;
    if (!el) return;
    const saved = scrollPositions.current.get(location.key);
    if (saved == null) {
      el.scrollTop = 0; // fresh navigation
      return;
    }
    // Content often loads async, so keep re-applying until the page is tall
    // enough to reach the saved offset (or we give up after ~1s).
    let raf = 0;
    let tries = 0;
    const restore = () => {
      el.scrollTop = saved;
      const reached = el.scrollHeight - el.clientHeight >= saved;
      if (!reached && tries++ < 60) raf = requestAnimationFrame(restore);
    };
    restore();
    return () => cancelAnimationFrame(raf);
    // Keyed by location.key (each navigation); pathname is only a same-page guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return (
    <LayoutProvider>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          overflow: "hidden",
        }}
      >
        <CssBaseline />

        {/* Header — always visible, in-flow, natural (constant) height. The
            Toolbar mixin's marginTop offsets the status bar; the AppBar bg fills
            that region. */}
        <AppBar
          enableColorOnDark
          position="static"
          color="inherit"
          elevation={0}
          sx={{
            flex: "0 0 auto",
            bgcolor: theme.palette.background.default,
          }}
        >
          <Toolbar>
            <Header />
          </Toolbar>
        </AppBar>

        {/* Main content — the only region that swaps between routes. It's the
            sole scroll container; Tug fills it exactly (height:100%). */}
        <Box
          component="main"
          ref={mainRef}
          id="app-main-scroll"
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            // Vertical scroll only -- content is always made to fit the width, so
            // a horizontal scrollbar should never appear on any page.
            overflowY: "auto",
            overflowX: "hidden",
            position: "relative",
            width: "100%",
            bgcolor: theme.palette.primary.light,
            p: defaultPadding ? "12px" : 0,
          }}
        >
          {isPublicPath ? (
            <Outlet />
          ) : (
            <AuthGuard>
              <Outlet />
            </AuthGuard>
          )}
        </Box>

        {/* Bottom tab bar — in-flow at the column bottom. Its own paddingBottom
            reserves exactly the ad-banner area (the fixed AdBanner / native admob
            overlay draws into that region at the screen bottom). This mirrors the
            known-good offsets from the old layout -- a separate spacer plus a
            safe-area inset over-reserved and left a gap above the ad. */}
        <BottomTabBar
          className="app-bottom-tab-bar"
          sx={{
            flex: "0 0 auto",
            paddingBottom:
              Capacitor.getPlatform() === "ios"
                ? `${adBannerOffset}px`
                : `calc(${adBannerOffset}px + env(safe-area-inset-bottom, 0px))`,
            zIndex: theme.zIndex.appBar,
          }}
        />
      </Box>

      {/* Profile pages pushed over the current list (portaled to <body>). */}
      <ProgressOverlayHost />
    </LayoutProvider>
  );
};

export default MainLayout;
